/**
 * useCategoryHandlers Hook
 * 
 * Handles category CRUD operations (create, edit, delete, details selection).
 */

import { TransactionCategory, TransactionCategoryGroup } from "../../../types";
import { api } from "../../../services/api";
import { isDemoMode } from "../../../utils/demoMode";

export interface UseCategoryHandlersParams {
  workspaceId: string;
  selectedTab: "expense" | "income";
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  setCategories: (categories: TransactionCategory[]) => void;
  setGroups: (groups: TransactionCategoryGroup[]) => void;
  // UI state setters
  setCategoryName: (name: string) => void;
  setCategoryGroupId: (id: string) => void;
  setGroupInputValue: (value: string) => void;
  setError: (error: string) => void;
  setCategoryNameError: (error: string) => void;
  setCategoryNameWarning: (warning: string) => void;
  setSaving: (saving: boolean) => void;
  setAddDialogOpen: (open: boolean) => void;
  setEditDialogOpen: (open: boolean) => void;
  setDeleteDialogOpen: (open: boolean) => void;
  setSelectDetailsDialogOpen: (open: boolean) => void;
  setEditingCategory: (category: TransactionCategory | null) => void;
  setCategoryToDelete: (category: TransactionCategory | null) => void;
  setCategoryForSelectDetails: (category: TransactionCategory | null) => void;
  setEnabledDetails: (details: string[]) => void;
  setDefaultValues: (values: Record<string, string | number>) => void;
  setOriginalEnabledDetails: (details: string[]) => void;
  setOriginalDefaultValues: (values: Record<string, string | number>) => void;
  // Current state values
  categoryName: string;
  categoryGroupId: string;
  groupInputValue: string;
  editingCategory: TransactionCategory | null;
  categoryToDelete: TransactionCategory | null;
}

export interface UseCategoryHandlersReturn {
  handleAddCategory: () => void;
  handleAddCategoryInGroup: (group: TransactionCategoryGroup) => void;
  handleEditCategory: (category: TransactionCategory) => void;
  handleDetailsCategory: (category: TransactionCategory) => Promise<void>;
  handleDeleteCategory: (category: TransactionCategory) => void;
  handleSaveCategory: () => Promise<void>;
  handleConfirmDelete: () => Promise<void>;
}

/**
 * Hook for category CRUD handlers
 */
export function useCategoryHandlers({
  workspaceId,
  selectedTab,
  categories,
  groups,
  setCategories,
  setGroups,
  setCategoryName,
  setCategoryGroupId,
  setGroupInputValue,
  setError,
  setCategoryNameError,
  setCategoryNameWarning,
  setSaving,
  setAddDialogOpen,
  setEditDialogOpen,
  setDeleteDialogOpen,
  setSelectDetailsDialogOpen,
  setEditingCategory,
  setCategoryToDelete,
  setCategoryForSelectDetails,
  setEnabledDetails,
  setDefaultValues,
  setOriginalEnabledDetails,
  setOriginalDefaultValues,
  categoryName,
  categoryGroupId,
  groupInputValue,
  editingCategory,
  categoryToDelete,
}: UseCategoryHandlersParams): UseCategoryHandlersReturn {
  const handleAddCategory = (): void => {
    setCategoryName("");
    setCategoryGroupId("");
    setGroupInputValue("");
    setError("");
    setCategoryNameError("");
    setCategoryNameWarning("");
    setAddDialogOpen(true);
  };

  const handleAddCategoryInGroup = (group: TransactionCategoryGroup): void => {
    setCategoryName("");
    setCategoryGroupId(group.id);
    setGroupInputValue(group.name);
    setError("");
    setCategoryNameError("");
    setCategoryNameWarning("");
    setEditingCategory(null);
    setAddDialogOpen(true);
  };

  const handleEditCategory = (category: TransactionCategory): void => {
    setCategoryName(category.name);
    const group = category.groupId ? groups.find((g) => g.id === category.groupId) : null;
    setCategoryGroupId(category.groupId || "");
    setGroupInputValue(group?.name || "");
    setEditingCategory(category);
    setError("");
    setEditDialogOpen(true);
  };

  const handleDetailsCategory = async (category: TransactionCategory): Promise<void> => {
    // Open selection dialog to choose which details to enable for this category
    // Use the latest category data from state to ensure we have the most up-to-date defaultValues
    // (important after group details are updated, as backend may have renamed detail names in defaultValues)
    const latestCategory = categories.find((c) => c.id === category.id) || category;
    
    const group = latestCategory.groupId ? groups.find((g) => g.id === latestCategory.groupId) : null;
    if (!group) {
      alert("Category must be assigned to a group to select details");
      return;
    }
    
    // Get current group detail names
    const currentGroupDetailNames = (group.details || []).map(d => d.name);
    
    // Initialize with existing category settings or empty
    const currentEnabled = latestCategory.enabledDetails || [];
    const rawDefaults = latestCategory.defaultValues || {};
    
    console.log(`[handleDetailsCategory] Category: ${latestCategory.name}`);
    console.log(`[handleDetailsCategory] Group detail names:`, currentGroupDetailNames);
    console.log(`[handleDetailsCategory] Category defaultValues keys:`, Object.keys(rawDefaults));
    console.log(`[handleDetailsCategory] Category defaultValues:`, rawDefaults);
    
    // Filter out empty default values when loading (clean up any empty strings in database)
    // Also map old detail names to new detail names if there's a mismatch
    // This handles cases where backend update didn't work or there's stale data
    // Also validate that list default values still exist in the current listItems
    const currentDefaults: Record<string, string | number> = {};
    Object.entries(rawDefaults).forEach(([key, value]) => {
      // Type guard: ensure value is string or number
      if (value !== "" && value !== null && value !== undefined && (typeof value === "string" || typeof value === "number")) {
        // Check if this key matches a current detail name
        if (currentGroupDetailNames.includes(key)) {
          // Key matches current detail name - validate the value
          const detail = group.details?.find((d) => d.name === key);
          if (detail) {
            if (detail.type === "list" && detail.listItems) {
              // For list types, validate that the value exists in listItems
              const valueStr = String(value);
              if (detail.listItems.includes(valueStr)) {
                // Value is valid - use it
                currentDefaults[key] = value;
                console.log(`[handleDetailsCategory] Default value "${key}": "${value}" is valid (exists in listItems)`);
              } else {
                // Value doesn't exist in listItems - try to find a similar match
                // Look for a value that starts with the same prefix or is similar
                const similarMatch = detail.listItems.find((item) => {
                  const itemLower = item.toLowerCase().trim();
                  const valueLower = valueStr.toLowerCase().trim();
                  
                  // Strategy 1: Exact match (case-insensitive)
                  if (itemLower === valueLower) return true;
                  
                  // Strategy 2: One contains the other (for typos like "Flashhh" -> "Flash" or "Flash" -> "Flashik")
                  if (itemLower.includes(valueLower) || valueLower.includes(itemLower)) return true;
                  
                  // Strategy 3: Share a common prefix (at least 3 characters)
                  // This handles cases like "Flashhh" -> "Flashik" (both start with "Flash")
                  const maxPrefixLength = Math.min(itemLower.length, valueLower.length);
                  if (maxPrefixLength >= 3) {
                    // Find the longest common prefix
                    let commonPrefixLength = 0;
                    for (let i = 0; i < maxPrefixLength; i++) {
                      if (itemLower[i] === valueLower[i]) {
                        commonPrefixLength++;
                      } else {
                        break;
                      }
                    }
                    // If they share at least 3 characters at the start, consider it a match
                    if (commonPrefixLength >= 3) return true;
                  }
                  
                  // Strategy 4: Levenshtein-like: check if they share most characters
                  // Count matching characters at the start
                  let matchingChars = 0;
                  const maxLen = Math.min(itemLower.length, valueLower.length);
                  for (let i = 0; i < maxLen; i++) {
                    if (itemLower[i] === valueLower[i]) {
                      matchingChars++;
                    } else {
                      break;
                    }
                  }
                  // If at least 70% of the shorter string matches, consider it a match
                  const matchRatio = matchingChars / Math.min(itemLower.length, valueLower.length);
                  if (matchRatio >= 0.7 && matchingChars >= 3) return true;
                  
                  return false;
                });
                
                if (similarMatch) {
                  // Found a similar match - use it
                  currentDefaults[key] = similarMatch;
                  console.log(`[handleDetailsCategory] Default value "${key}": "${value}" not found in listItems, mapped to similar value "${similarMatch}"`);
                } else {
                  // No similar match found - try one more strategy: use the first item as fallback
                  // This preserves the concept of having a default, even if the exact value changed
                  if (detail.listItems.length > 0) {
                    // Use the first item as a fallback to maintain the default selection behavior
                    currentDefaults[key] = detail.listItems[0];
                    console.warn(`[handleDetailsCategory] Default value "${key}": "${value}" not found in listItems and no similar match. Using first item "${detail.listItems[0]}" as fallback. Available items:`, detail.listItems);
                  } else {
                    // No items available - clear the default value
                    console.warn(`[handleDetailsCategory] Default value "${key}": "${value}" not found in listItems and list is empty. Clearing default.`);
                    // Don't add to currentDefaults - this clears the invalid default
                  }
                }
              }
            } else {
              // For measurable types, accept any value
              currentDefaults[key] = value;
              console.log(`[handleDetailsCategory] Default value "${key}": "${value}" is valid (measurable type)`);
            }
          } else {
            // Detail not found (shouldn't happen)
            console.warn(`[handleDetailsCategory] Detail "${key}" not found in group details`);
          }
        } else {
          // Key doesn't match - might be an old name
          console.log(`[handleDetailsCategory] Default value key "${key}" doesn't match current detail names. Trying to find match...`);
          
          // Try to find a detail with similar properties (for list types, check if value is in listItems)
          const matchingDetail = group.details?.find((detail) => {
            if (detail.name === key) return false; // Already checked
            if (detail.type === "list" && detail.listItems) {
              // Check if the value matches one of the list items
              return detail.listItems.includes(String(value));
            }
            // For measurable types, we can't reliably match by value alone
            return false;
          });
          
          if (matchingDetail) {
            // Found a matching detail - use the new name
            console.log(`[handleDetailsCategory] Mapped old default key "${key}" (value: "${value}") to new detail name "${matchingDetail.name}"`);
            currentDefaults[matchingDetail.name] = value;
          } else {
            // No match found - keep the old key but it won't be displayed
            // This shouldn't happen if backend update worked correctly
            console.warn(`[handleDetailsCategory] Default value key "${key}" (value: "${value}") doesn't match any current detail name. Available names:`, currentGroupDetailNames);
            currentDefaults[key] = value;
          }
        }
      }
    });
    
    console.log(`[handleDetailsCategory] Final mapped defaultValues:`, currentDefaults);
    
    setCategoryForSelectDetails(latestCategory);
    setEnabledDetails([...currentEnabled]);
    setDefaultValues({ ...currentDefaults });
    setOriginalEnabledDetails([...currentEnabled]);
    setOriginalDefaultValues({ ...currentDefaults });
    setSelectDetailsDialogOpen(true);
  };

  const handleDeleteCategory = (category: TransactionCategory): void => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleSaveCategory = async (): Promise<void> => {
    if (!categoryName.trim()) {
      setError("Category name is required");
      return;
    }

    let finalGroupId = categoryGroupId;

    // If groupInputValue doesn't match an existing group, create a new one
    if (groupInputValue.trim() && !categoryGroupId) {
      // Check if input matches an existing group name (case-insensitive)
      const matchingGroup = groups.find(
        (g) => g.name.toLowerCase() === groupInputValue.trim().toLowerCase()
      );
      
      if (matchingGroup) {
        finalGroupId = matchingGroup.id;
      } else {
        // Create new group
        try {
          const newGroup = await api.createGroup(workspaceId, groupInputValue.trim(), selectedTab);
          finalGroupId = newGroup.id;
          // Refresh groups
          const groupsData = await api.getGroups(workspaceId, selectedTab);
          setGroups(groupsData);
        } catch (err: any) {
          setError(err.message || "Failed to create group");
          return;
        }
      }
    }

    if (!finalGroupId || !finalGroupId.trim()) {
      setError("Group is required");
      return;
    }

    setSaving(true);
    setError("");
    setCategoryNameError("");
    setCategoryNameWarning("");

    try {
      if (editingCategory) {
        // Update existing category
        await api.updateCategory(workspaceId, editingCategory.id, categoryName.trim(), finalGroupId);
        setEditDialogOpen(false);
        setEditingCategory(null);
        // Refresh categories
        const data = await api.getCategories(workspaceId, selectedTab);
        setCategories(data);
      } else {
        // Create new category
        await api.createCategory(workspaceId, categoryName.trim(), selectedTab, finalGroupId);
        setAddDialogOpen(false);
        // Refresh categories
        const data = await api.getCategories(workspaceId, selectedTab);
        setCategories(data);
      }
      setCategoryName("");
      setCategoryGroupId("");
      setGroupInputValue("");
    } catch (err: any) {
      setError(err.message || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!categoryToDelete) return;

    setSaving(true);
    setError("");

    try {
      await api.deleteCategory(workspaceId, categoryToDelete.id);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      // Refresh categories
      const data = await api.getCategories(workspaceId, selectedTab);
      setCategories(data);
    } catch (err: any) {
      setError(err.message || "Failed to delete category");
    } finally {
      setSaving(false);
    }
  };

  return {
    handleAddCategory,
    handleAddCategoryInGroup,
    handleEditCategory,
    handleDetailsCategory,
    handleDeleteCategory,
    handleSaveCategory,
    handleConfirmDelete,
  };
}
